import { Select, Switch, Tooltip } from 'antd'
import { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ServerPrivateRegionNodeSpec, ServerPrivateRegionVGpuSpec } from '@/api/type-server'
import InfoIcon from '@/assets/icons/info-i.svg?react'

import { SpecCategory, SpecCategoryType } from '../constants/specCategory'

interface EditableSpecSectionProps {
  spec: ServerPrivateRegionNodeSpec
  category: SpecCategoryType
  onChange: (spec: ServerPrivateRegionNodeSpec) => void
  disabled?: boolean
  vgpuSpecs?: ServerPrivateRegionVGpuSpec[]
}

// Category config with label and color
const CATEGORY_CONFIG: Record<SpecCategoryType, { label: string; color: string }> = {
  [SpecCategory.VGPU]: { label: 'vGPU', color: 'bg-btn-tertiary' },
  [SpecCategory.GPU]: { label: 'GPU Passthrough', color: 'bg-btn-secondary' },
  [SpecCategory.CPU]: { label: 'vCPU', color: 'bg-active' },
}

// Input component with unit suffix inside
function SpecInput({
  value,
  onChange,
  suffix,
  disabled,
  min,
  max,
  required = false,
}: {
  value: number
  onChange: (value: number) => void
  suffix?: string
  disabled?: boolean
  min?: number
  max?: number
  required?: boolean
}) {
  const { t } = useTranslation('privateRegion')
  const [displayValue, setDisplayValue] = useState(() =>
    value === 0 && required ? '' : String(value),
  )
  const [isFocused, setIsFocused] = useState(false)

  // Sync from external value changes only when not focused
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value === 0 && required ? '' : String(value))
    }
  }, [value, isFocused, required])

  const showError = required && displayValue === ''

  return (
    <div className="flex flex-col gap-1">
      <div
        className={`flex items-center border rounded-lg h-11 px-4 gap-2 ${showError ? 'border-error' : 'border-neutral-200'} ${disabled ? 'bg-neutral-50' : 'bg-panel-white'}`}
      >
        <input
          type="number"
          value={displayValue}
          onChange={(e) => {
            const raw = e.target.value
            if (raw === '') {
              setDisplayValue('')
              onChange(0)
              return
            }
            const val = Number(raw)
            if (min !== undefined && val < min) return
            if (max !== undefined && val > max) return
            setDisplayValue(raw)
            onChange(val)
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className={`flex-1 outline-none t-sb bg-transparent min-w-0 ${disabled ? 'text-tertiary cursor-not-allowed' : 'text-primary'}`}
          disabled={disabled}
          min={min}
          max={max}
        />
        {suffix && (
          <span className={`t-sb shrink-0 ${disabled ? 'text-tertiary' : 'text-primary'}`}>
            {suffix}
          </span>
        )}
      </div>
      {showError && <span className="t-xs text-error">{t('editInstanceSpec.fieldRequired')}</span>}
    </div>
  )
}

// Field component with label and optional tooltip
function EditField({
  label,
  tooltip,
  children,
  className = '',
}: {
  label: string
  tooltip?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`flex flex-col gap-2 flex-1 min-w-0 ${className}`}>
      <div className="flex items-center gap-1">
        <span className="t-sb text-primary">{label}</span>
        {tooltip && (
          <Tooltip
            title={tooltip}
            classNames={{
              root: 't-xs max-w-[180px] rounded-md',
            }}
            styles={{
              body: { textAlign: 'center' },
            }}
          >
            <InfoIcon className="w-4 h-4 text-tertiary cursor-help" />
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  )
}

export const EditableSpecSection = memo(function EditableSpecSection({
  spec,
  category,
  onChange,
  disabled = false,
  vgpuSpecs,
}: EditableSpecSectionProps) {
  const { t } = useTranslation('privateRegion')

  const isVGPU = category === SpecCategory.VGPU
  const isGPU = category === SpecCategory.GPU
  const isCPU = category === SpecCategory.CPU
  const config = CATEGORY_CONFIG[category]

  const handleEnabledChange = useCallback(
    (checked: boolean) => {
      if (checked && isVGPU && vgpuSpecs?.length && !spec.vgpuSpecId) {
        const defaultSpec = vgpuSpecs[0]
        onChange({
          ...spec,
          enabled: checked,
          vgpuSpecId: defaultSpec.resID,
          gpuNum: 1,
          gpuRamMB: defaultSpec.vGpuRamMB,
        })
      } else {
        onChange({ ...spec, enabled: checked })
      }
    },
    [spec, onChange, isVGPU, vgpuSpecs],
  )

  const handleUnitsChange = useCallback(
    (value: number) => {
      onChange({ ...spec, units: value })
    },
    [spec, onChange],
  )

  const handleGpuNumChange = useCallback(
    (value: number) => {
      onChange({ ...spec, gpuNum: value })
    },
    [spec, onChange],
  )

  const handleVgpuGpcChange = useCallback(
    (value: string | null) => {
      const selectedVgpuSpec = vgpuSpecs?.find((s) => s.resID === value)
      onChange({
        ...spec,
        vgpuSpecId: value ?? '',
        ...(selectedVgpuSpec && {
          gpuNum: 1,
          gpuRamMB: selectedVgpuSpec.vGpuRamMB,
        }),
      })
    },
    [spec, onChange, vgpuSpecs],
  )

  const handleCpuCoresChange = useCallback(
    (value: number) => {
      onChange({ ...spec, cpuCores: value })
    },
    [spec, onChange],
  )

  const handleRamMBChange = useCallback(
    (value: number) => {
      // Convert GB input to MB for storage
      onChange({ ...spec, ramMB: value * 1024 })
    },
    [spec, onChange],
  )

  const handleDiskGBChange = useCallback(
    (value: number) => {
      onChange({ ...spec, diskGB: value })
    },
    [spec, onChange],
  )

  // Note: disabled state is handled by pointer-events-none on the container
  // We don't pass disabled to child components to avoid greyed-out styling

  // vGPU GPC options from API
  const vgpuGpcOptions = (vgpuSpecs ?? []).map((s) => ({
    value: s.resID,
    label: s.title,
  }))

  return (
    <div className="bg-panel-white border border-neutral-200 rounded-lg px-6 py-4">
      <div className={`flex flex-col gap-4 ${disabled ? 'pointer-events-none' : ''}`}>
        {/* Section Header with colored indicator and Toggle */}
        <div className="flex items-center gap-2 py-2">
          <div className={`w-1 h-5 rounded-sm ${config.color}`} />
          <span className="flex-1 t-bold text-primary">{config.label} Spec</span>
          <Switch
            checked={spec.enabled}
            onChange={handleEnabledChange}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </div>

        {/* Content - only shown when enabled */}
        {spec.enabled && (
          <>
            {/* Row 1: Spec Unit, GPU Number (vGPU/GPU), placeholders */}
            <div className="flex gap-4">
              {/* Spec Unit */}
              <EditField
                label={t('instanceSpec.specUnit')}
                tooltip={t('instanceSpec.specUnitTooltip')}
              >
                <SpecInput
                  value={spec.units}
                  onChange={handleUnitsChange}
                  min={1}
                  max={999999}
                  suffix="Instances"
                  required
                />
              </EditField>

              {/* GPU Number - for vGPU and GPU modes */}
              {(isVGPU || isGPU) && (
                <EditField
                  label={t(isVGPU ? 'instanceSpec.vgpuNumber' : 'instanceSpec.gpuNumber')}
                  tooltip={t(
                    isVGPU ? 'instanceSpec.vgpuNumberTooltip' : 'instanceSpec.gpuNumberTooltip',
                  )}
                >
                  <SpecInput
                    value={spec.gpuNum}
                    onChange={handleGpuNumChange}
                    min={1}
                    max={999999}
                    disabled={isVGPU}
                    required={isGPU}
                  />
                </EditField>
              )}

              {/* vGPU GPC dropdown - fills 3rd column in vGPU mode */}
              {isVGPU && (
                <EditField
                  label={t('instanceSpec.vgpuGpc')}
                  tooltip={t('instanceSpec.vgpuGpcTooltip')}
                >
                  <Select
                    value={spec.vgpuSpecId || undefined}
                    onChange={handleVgpuGpcChange}
                    options={vgpuGpcOptions}
                    className="w-full h-11 [&_.ant-select-selector]:!h-11 [&_.ant-select-selector]:!py-0 [&_.ant-select-selection-item]:!leading-[42px]"
                    placeholder="Select"
                    status={spec.enabled && !spec.vgpuSpecId ? 'error' : undefined}
                  />
                </EditField>
              )}

              {/* Placeholder to maintain 3-column layout */}
              {isGPU && <div className="flex-1 min-w-0 opacity-0" />}
              {isCPU && (
                <>
                  <div className="flex-1 min-w-0 opacity-0" />
                  <div className="flex-1 min-w-0 opacity-0" />
                </>
              )}
            </div>

            {/* Row 2: vCPU, Memory, Storage Allocation */}
            <div className="flex gap-4">
              {/* vCPU Allocation */}
              <EditField label={t('instanceSpec.vcpuAllocation')}>
                <SpecInput
                  value={spec.cpuCores}
                  onChange={handleCpuCoresChange}
                  min={1}
                  max={999999}
                  suffix="Cores"
                  required
                />
              </EditField>

              {/* Memory Allocation (display in GB, store in MB) */}
              <EditField label={t('instanceSpec.memoryAllocation')}>
                <SpecInput
                  value={Math.round(spec.ramMB / 1024)}
                  onChange={handleRamMBChange}
                  min={1}
                  max={999999}
                  suffix="GB"
                  required
                />
              </EditField>

              {/* Storage Allocation */}
              <EditField label={t('instanceSpec.storageAllocation')}>
                <SpecInput
                  value={spec.diskGB}
                  onChange={handleDiskGBChange}
                  min={1}
                  max={999999}
                  suffix="GB"
                  required
                />
              </EditField>
            </div>
          </>
        )}
      </div>
    </div>
  )
})
